// src/components/Introduction.tsx
import * as React from "react";
import { Card, CardContent } from "../ui/card";
import { Building2 } from "lucide-react";

type Stat = {
  value: string | number;
  label: string;
  icon?: React.ReactNode; // optional small icon inside the stat pill
};

export type IntroductionProps = {
  /** Main title (e.g., portal name) */
  title: string;
  /** Subtitle / territory line */
  subtitle?: string;
  /** Primary icon in the left circle (defaults to Building2) */
  leftIcon?: React.ReactNode;
  /** Big emoji or node on the right (shown only on lg+) */
  rightEmoji?: React.ReactNode;
  /** One “highlight” stat (kept like your design) */
  stat: Stat;
  /** Optional: card gradient override */
  gradientClassName?: string; // default matches your snippet
  /** Optional: className for outer Card */
  className?: string;
  /** Optional: max width container width class */
  maxWidthClassName?: string;
};

export const Introduction: React.FC<IntroductionProps> = ({
  title,
  subtitle,
  leftIcon,
  rightEmoji,
  stat,
  gradientClassName = "bg-gradient-to-r from-orange-50 via-amber-50 to-orange-100 border-orange-200",
  className = "",
  maxWidthClassName = "max-w-xs",
}) => {
  return (
    <Card className={`${gradientClassName} overflow-hidden ${className}`}>
      <CardContent className="p-8">
        <div className="flex items-center justify-between">
          {/* Left side */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-travelin-warm flex items-center justify-center">
                {leftIcon ?? <Building2 className="w-8 h-8 text-white" />}
              </div>
              <div>
                <h1 className="text-xxl font-medium text-foreground mb-1">
                  {title}
                </h1>
                {subtitle && (
                  <p className="text-l text-muted-foreground">{subtitle}</p>
                )}
              </div>
            </div>

            {/* Highlight stat pill */}
            <div className={maxWidthClassName}>
              <div className="bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-white/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    {/* Small icon inside the stat bubble */}
                    {stat.icon ?? <Building2 className="w-5 h-5 text-orange-600" />}
                  </div>
                  <div>
                    <p className="text-xxl font-medium text-orange-600">{stat.value}</p>
                    <p className="text-s text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right big emoji / illustration */}
          <div className="hidden lg:block ml-8">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
              <div className="text-xxl">{rightEmoji ?? "🏔️"}</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
